import { useState, useRef } from "react";
import { useUploadPackage } from "../../../api/routes/updates";

const UpdatePackageUploaderRoute = () => {
    const [version, setVersion] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);

    const uploadPackageMutation = useUploadPackage();

    const handleVersionChange = (e) => {
        setVersion(e.target.value);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setFileName(file.name);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate inputs
        if (!version) {
            setError("Version is required");
            return;
        }

        if (!selectedFile) {
            setError("Please select a package file");
            return;
        }

        // Clear previous states
        setError("");
        setUploading(true);
        setUploadResult(null);

        // Create form data
        const formData = new FormData();
        formData.append("version", version);
        formData.append("packageFile", selectedFile);

        try {
            // Update the mutationFn to include the formData
            uploadPackageMutation.mutate(formData, {
                onSuccess: (data) => {
                    console.log(data);
                    setUploadResult(data);
                    setSelectedFile(null);
                    setFileName("");
                    setVersion("");
                    if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                    }
                },
                onError: (err) => {
                    setError(err.message || "Failed to upload package");
                },
            });
        } catch (err) {
            setError("Error uploading package: " + (err.message || "Unknown error"));
        } finally {
            setUploading(false);
        }
    };

    const handleReset = () => {
        setVersion("");
        setSelectedFile(null);
        setFileName("");
        setError("");
        setUploadResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <div className="w-full max-w-lg mx-auto p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Upload Update Package</h2>

            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}

            {uploadResult && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                    <p className="font-bold">Upload Successful!</p>
                    <p>Version: {uploadResult?.data?.data?.version}</p>
                    <p
                        className="flex-wrap break-all text-sm mt-1
                        
                    "
                    >
                        Checksum: {uploadResult?.data?.data?.checksum}
                    </p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label
                        htmlFor="version"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Version
                    </label>
                    <input
                        type="text"
                        id="version"
                        value={version}
                        onChange={handleVersionChange}
                        placeholder="e.g., 1.0.1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                <div>
                    <label
                        htmlFor="packageFile"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Package File (.tar.gz)
                    </label>
                    <div className="flex items-center space-x-2">
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700 transition-colors"
                        >
                            Select File
                        </button>
                        <span className="text-sm text-gray-500 truncate max-w-xs">
                            {fileName || "No file selected"}
                        </span>
                        <input
                            type="file"
                            id="packageFile"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept=".tar.gz"
                            className="hidden"
                        />
                    </div>
                </div>

                <div className="flex space-x-4 pt-2">
                    <button
                        type="submit"
                        disabled={uploading}
                        className={`px-4 py-2 rounded-md text-white ${
                            uploading
                                ? "bg-blue-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                        } transition-colors`}
                    >
                        {uploading ? "Uploading..." : "Upload Package"}
                    </button>

                    <button
                        type="button"
                        onClick={handleReset}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700 transition-colors"
                    >
                        Reset
                    </button>
                </div>
            </form>
        </div>
    );
};

export { UpdatePackageUploaderRoute };
